import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useIntl, defineMessages } from "react-intl";

import {
  DocumentBundleMembershipRole,
  type PageTypes,
  type SignerRoleInput as SignerRole,
} from "graphql_globals";
import type { ReduxState } from "redux/reducers/app";
import { toolPlaced } from "redux/actions/pdf_menu";
import { useCurrentToolData } from "common/pdf_menu";
import { UNASSIGNED_SIGNER_INDEX } from "constants/annotations";
import { useRecipientColors } from "common/pdf/recipient_colors/context";
import type { DrawableDesignation } from "common/pdf/pspdfkit/designation";
import type { DesignationOnFulfill } from "common/pdf/designation";

export type CurrentToolData = NonNullable<ReduxState["pdfMenu"]["currentToolData"]>;
export type PagePressTool = {
  canHandle: (currentToolData: CurrentToolData) => boolean;
  handler: (handlerData: {
    pageIndex: number;
    point: { x: number; y: number };
    pageType: PageTypes;
    currentToolData: CurrentToolData;
  }) => Promise<unknown> | void;
};
export type DesignationFulfiller<Designation extends DrawableDesignation> = {
  canHandle: (designation: Designation) => boolean;
  handler: (designation: Designation) => ReturnType<DesignationOnFulfill<Designation>>;
};
export type VectorGraphicSubtype = "SIGNATURE" | "INITIALS";

const DEFAULT_VECTOR_GRAPHIC_HEIGHT = 14;

// the placeholder is just text description - placeholders are incremented for last name in other fns
const USE_PLACEHOLDER_SIGNER_MESSAGES = defineMessages({
  signer: {
    id: "bfacaf44-c245-4aef-b202-5de27516abd2",
    defaultMessage: "Signer",
  },
});

function usePlaceholderData() {
  const intl = useIntl();
  const recipientColors = useRecipientColors();
  return [
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[0].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.PRIMARY,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[1].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SECONDARY,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[2].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_3,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[3].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_4,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[4].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_5,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[5].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_6,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[6].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_7,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[7].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_8,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[8].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_9,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
    {
      name: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
      colorHex: recipientColors.signers[9].text,
      signerRole: {
        index: UNASSIGNED_SIGNER_INDEX.SIGNER_10,
        role: DocumentBundleMembershipRole.SIGNER,
      },
    },
  ];
}

export function validVectorGraphicSubtype(type: string | undefined): type is VectorGraphicSubtype {
  return type === "SIGNATURE" || type === "INITIALS";
}

export function scaleVectorGraphicSize({
  size: { height, width },
  maxSize = { height: DEFAULT_VECTOR_GRAPHIC_HEIGHT },
}: {
  size: { height: number; width: number };
  maxSize?: { height: number; width?: number };
}) {
  let size = { height, width };
  const ratio = size.width / size.height;

  if (size.height > maxSize.height) {
    size = { height: maxSize.height, width: maxSize.height * ratio };
  }

  if (maxSize.width && size.width > maxSize.width) {
    size = { height: maxSize.width / ratio, width: maxSize.width };
  }

  return size;
}

export function scaleVectorGraphicLayout({
  size: { height, width },
  point: { x, y },
  maxSize = { height: DEFAULT_VECTOR_GRAPHIC_HEIGHT },
}: {
  size: { height: number; width: number };
  point: { x: number; y: number };
  maxSize?: { height: number; width?: number };
}) {
  const size = scaleVectorGraphicSize({ size: { height, width }, maxSize });
  const point = { x, y };

  if (size.height < maxSize.height) {
    // align to bottom of max rect
    // TODO: fulfillDesignationMutation instead of client calculations - would match batch sign
    point.y -= maxSize.height - size.height;
  }

  return { size, point };
}

export function usePlaceholderSigners() {
  const intl = useIntl();
  return usePlaceholderData().map((sd, index) => ({
    firstName: intl.formatMessage(USE_PLACEHOLDER_SIGNER_MESSAGES.signer),
    middleName: null,
    lastName: (index + 1).toString(),
    colorHex: sd.colorHex,
    signerRole: sd.signerRole,
  }));
}

export function useDefaultSignerNames(
  signers: {
    firstName: string | null;
    lastName: string | null;
    signerRole: SignerRole;
  }[],
) {
  const placeholders = usePlaceholderSigners();
  if (signers.length < 1 || signers.every((signer) => !signer.firstName)) {
    return placeholders;
  }
  return signers.map((signer, i) => {
    const placeholder = placeholders.at(i);
    return {
      ...signer,
      firstName: signer.firstName ?? placeholder!.firstName,
      lastName: signer.lastName ?? placeholder!.lastName,
    };
  });
}

export function usePagePress(...tools: PagePressTool[]) {
  const currentToolData = useCurrentToolData();
  const dispatch = useDispatch();

  return useCallback(
    ({
      pageIndex,
      point,
      pageType,
      onPlace,
    }: {
      pageIndex: number;
      pageType: PageTypes;
      point: { x: number; y: number };
      onPlace?: (currentToolData: CurrentToolData) => void;
    }) => {
      if (!currentToolData) {
        return;
      }

      dispatch(toolPlaced());
      if (onPlace) {
        onPlace(currentToolData);
      }
      const tool = tools.find((tool) => tool.canHandle(currentToolData));
      if (!tool) {
        throw new Error(
          `unhandled tool: ${currentToolData.placementType}, ${currentToolData.type}`,
        );
      }
      return tool.handler({
        pageIndex,
        point,
        pageType,
        currentToolData,
      });
    },
    [...tools, currentToolData],
  );
}

export function useFulfillDesignation<Designation extends DrawableDesignation>(
  ...fulfillers: DesignationFulfiller<Designation>[]
): DesignationOnFulfill<Designation> {
  return useCallback((designation) => {
    const fulfiller = fulfillers.find((fulfiller) => fulfiller.canHandle(designation));
    if (!fulfiller) {
      throw new Error(`unhandled designation: ${designation.type}`);
    }
    return fulfiller.handler(designation);
  }, fulfillers);
}
